/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-08-12 10:27:50
 *
 *******************************************************/
//@flow
import _ from 'lodash';
import moment from 'moment';
import React, { Component } from 'react';
import {
  Button,
  Popover,
  ActionList,
  Stack,
  FormLayout,
  DatePicker,
  TextField,
  Checkbox,
  Badge,
  Icon,
  TextStyle,
  InlineError
} from '@shopify/polaris';
import { CalendarMajor, LockMinor, SelectMinor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import {
  getStoreDate,
  buildDateFromDateRange,
  buildDateRangeFromDate,
  buildCompareDateRange,
  displayDateRange,
  buildDateRangeWithLimitDays
} from 'src/helpers/analytics-helpers';
import { DATE_FORMAT } from 'src/constants/language-setting';
import { NUMBER_DAYS_LIMIT } from 'src/constants/analytics';

type Props = {
  fromDate: Date,
  toDate: Date,
  compare: boolean,
  onChooseDate: Function,
  appLanguage: string,
  showCompare: boolean
};

type State = {
  popoverActive: boolean,
  selectedDateRange: string,
  startDate: string,
  endDate: string,
  month: number,
  year: number,
  selectedDates: {
    start: Date,
    end: Date
  },
  disableDatesAfter: ?Date,
  disableDatesBefore: ?Date,
  compare: boolean
};

type ComposedProps = Props & WithI18nProps;
class RenderDateRangePickerComponent extends Component<ComposedProps, State> {
  state = {
    popoverActive: false,
    popoverSelectActive: false,
    // Dropdown date value
    selectedDateRange: 'none',
    // Date changed from input
    startDate: '',
    endDate: '',

    // Date picker
    month: getStoreDate(new Date()).getMonth(),
    year: getStoreDate(new Date()).getFullYear(),
    selectedDates: {
      start: getStoreDate(new Date()),
      end: getStoreDate(new Date())
    },
    disableDatesAfter: null,
    disableDatesBefore: null,

    compare: false
  };

  componentDidMount() {
    const { compare, fromDate, toDate } = this.props;

    this.initialValue(fromDate, toDate, compare);
  }

  componentWillReceiveProps(nextProps) {
    const { compare: prevCompare, prevFromDate, prevToDate } = this.props;
    const { compare, fromDate, toDate } = nextProps;

    if (compare !== prevCompare || fromDate !== prevFromDate || toDate !== prevToDate) {
      this.initialValue(fromDate, toDate, compare);
    }
  }

  render() {
    const {
      popoverActive,
      selectedDateRange,
      month,
      year,
      selectedDates,
      startDate,
      endDate,
      disableDatesAfter,
      disableDatesBefore,
      popoverSelectActive,
      compare
    } = this.state;

    const {
      disabled,
      fromDate,
      toDate,
      limitDays,
      allowTrialAddon,
      showCompare = true,
      compare: defaultCompare,
      disableLockUnLock,
      i18n
    } = this.props;
    const actionActiveAddonLabel = allowTrialAddon ? 'startTrialAction' : 'unlockActionViewMore';

    const defaultDateRange = buildDateRangeFromDate(fromDate, toDate);
    const dateRange = buildDateRangeWithLimitDays(NUMBER_DAYS_LIMIT).map(function (range) {
      const { days } = range;
      return {
        ...range,
        label:
          days > limitDays ? (
            <p className="action-locked action-locked--analytics icon-warning ">
              <Icon source={LockMinor} color="base" />
              {i18n.translate(range.label)}
              <Badge status="warning">
                <span className="title">{i18n.translate('AdminHTML.Common.Badge.premium')}</span>
              </Badge>
            </p>
          ) : (
            i18n.translate(range.label)
          ),
        disabled: days > limitDays
      };
    });
    if (limitDays < NUMBER_DAYS_LIMIT && !disableLockUnLock) {
      dateRange.push({
        label: (
          <p style={{ color: '#2C6ECB' }}>
            {i18n.translate(`AdminHTML.Analytics.AnalyticsV2.${actionActiveAddonLabel}`)}
          </p>
        ),
        value: '',
        action: () => this.openReviewAddonModal()
      });
    }
    const selectedDateRangeObject = _.find(dateRange, { value: defaultDateRange });
    const selectedDateRangeLabel = selectedDateRangeObject ? selectedDateRangeObject.label : '';
    const dayDiff = moment(selectedDates.end).diff(moment(selectedDates.start), 'days');

    const currentDateRangeSelected = dateRange.find(e => e.value === selectedDateRange);

    const isDisabledCheckbox = limitDays <= _.get(currentDateRangeSelected, 'days', 0);

    // const isDisabledCheckBox =
    const activator = (
      <div className="date-select-picker">
        <Button
          disabled={disabled}
          onClick={this.togglePopoverActive}
          icon={CalendarMajor}
          textAlign="left"
          fullWidth={showCompare ? false : true}>
          {defaultDateRange !== 'custom'
            ? selectedDateRangeLabel
            : displayDateRange(fromDate, toDate)}
        </Button>
        {showCompare && defaultCompare ? (
          <span className="compare-time">
            <TextStyle variation="subdued">
              {i18n.translate('AdminHTML.Analytics.RenderDateRangePicker.activator', {
                nt_date_range: buildCompareDateRange(fromDate, toDate, defaultDateRange)
              })}
            </TextStyle>
          </span>
        ) : (
          ''
        )}
      </div>
    );
    return (
      <div className="datePicker">
        <Popover
          preventCloseOnChildOverlayClick
          active={popoverActive}
          activator={activator}
          onClose={e => this.closePopover(e)}
          sectioned>
          <Popover.Pane>
            <Popover.Section>
              <FormLayout>
                <div>
                  <Popover
                    active={popoverSelectActive}
                    fullWidth
                    activator={
                      <div className="analytics-select-popover">
                        <p>
                          {i18n.translate('AdminHTML.Analytics.RenderDateRangePicker.selectLabel')}
                        </p>
                        <Button onClick={this.togglePopoverSelectDateRange}>
                          <p>{dateRange.find(e => e.value === selectedDateRange)?.label}</p>
                          <Icon source={SelectMinor} />
                        </Button>
                      </div>
                    }
                    onClose={this.togglePopoverSelectDateRange}>
                    <ActionList
                      items={dateRange.map(range => ({
                        content: range.label,
                        disabled: range.disabled,
                        onAction: range.action
                          ? range.action
                          : () => {
                              this.handleSelectChange(range.value);
                              this.togglePopoverSelectDateRange();
                            }
                      }))}
                    />
                  </Popover>
                </div>
                <FormLayout.Group>
                  <TextField
                    label={i18n.translate(
                      'AdminHTML.Analytics.RenderDateRangePicker.textField_Label_Starting'
                    )}
                    value={startDate}
                    onChange={this.handleChangeStarting}
                    onBlur={this.handleBlurStarting}
                  />
                  <TextField
                    label={i18n.translate(
                      'AdminHTML.Analytics.RenderDateRangePicker.textField_Label_Ending'
                    )}
                    value={endDate}
                    onChange={this.handleChangeEnding}
                    onBlur={this.handleBlurEnding}
                  />
                </FormLayout.Group>
                {/* Message when user chose date range > limit days*/}
                {dayDiff >= limitDays ? (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      marginTop: '-17px',
                      marginBottom: '10px'
                    }}>
                    <InlineError
                      message={i18n.translate(
                        'AdminHTML.Analytics.RenderDateRangePicker.inlineError',
                        { nt_number: limitDays }
                      )}
                      fieldID="selectDateRange"
                    />
                    {limitDays < NUMBER_DAYS_LIMIT && !disableLockUnLock && (
                      <p
                        onClick={this.openReviewAddonModal}
                        style={{
                          color: '#2C6ECB',
                          cursor: 'pointer'
                        }}>
                        {i18n.translate(
                          `AdminHTML.Analytics.AnalyticsV2.${actionActiveAddonLabel}`
                        )}
                      </p>
                    )}
                  </div>
                ) : (
                  ''
                )}
                <DatePicker
                  month={month}
                  year={year}
                  onChange={this.setSelectedDates}
                  onMonthChange={this.handleMonthChange}
                  selected={selectedDates}
                  multiMonth={true}
                  allowRange={true}
                  disableDatesAfter={disableDatesAfter}
                  disableDatesBefore={disableDatesBefore}
                />
                {showCompare && (
                  <Checkbox
                    disabled={isDisabledCheckbox}
                    label={i18n.translate(
                      'AdminHTML.Analytics.RenderDateRangePicker.checkbox_Label'
                    )}
                    checked={compare}
                    onChange={this.handleChangeCompare}
                  />
                )}
              </FormLayout>
            </Popover.Section>
          </Popover.Pane>

          <Popover.Pane fixed>
            <Popover.Section>
              <div className="BC-Popover__Button">
                <Stack>
                  <Button onClick={this.closePopover}>
                    {i18n.translate('AdminHTML.Common.Action.cancel')}
                  </Button>
                  <Button
                    primary
                    onClick={this.handleApply}
                    disabled={dayDiff >= NUMBER_DAYS_LIMIT}>
                    {i18n.translate('AdminHTML.Common.Action.apply')}
                  </Button>
                </Stack>
              </div>
            </Popover.Section>
          </Popover.Pane>
        </Popover>
      </div>
    );
  }

  // Toggle state to show/hide the popover
  togglePopoverActive = () => {
    this.setState(({ popoverActive }) => {
      return { popoverActive: !popoverActive };
    });
  };

  // Toggle state to show/hide the popover select date range
  togglePopoverSelectDateRange = () => {
    this.setState(({ popoverSelectActive }) => {
      return { popoverSelectActive: !popoverSelectActive };
    });
  };

  // initial value for date in popover
  initialValue = (fromDate, toDate, compare) => {
    const { appLanguage } = this.props;
    const formatDateNumber = DATE_FORMAT[appLanguage].number;
    // Initial value
    const today = getStoreDate(new Date());
    const lastLimitDays = moment(today).add(-NUMBER_DAYS_LIMIT, 'days').toDate();

    const selectedDateRange = buildDateRangeFromDate(fromDate, toDate);

    let startDate = moment(fromDate).format(formatDateNumber);
    let endDate = moment(toDate).format(formatDateNumber);

    let selectedDates = {};
    selectedDates.start = moment(fromDate).toDate();
    selectedDates.end = moment(toDate).toDate();

    // Set month, year for the date picker, the selected is the last month
    // so that the date picker show last month and current month
    let month = today.getMonth();
    month = month - 1 < 0 ? 11 : month - 1;
    let year = today.getFullYear();
    year = month === 11 ? year - 1 : year;

    this.setState({
      selectedDateRange,
      startDate,
      endDate,
      selectedDates,
      disableDatesBefore: lastLimitDays,
      disableDatesAfter: today,
      month,
      year,
      compare
    });
  };

  // Handle cancel/close popover
  closePopover = e => {
    if (e === 0) return;

    const { fromDate, toDate, compare } = this.props;

    //reset value
    this.initialValue(fromDate, toDate, compare);

    this.togglePopoverActive();
  };

  // Handle set range select
  handleSelectChange = selectedDateRange => {
    const dataState = buildDateFromDateRange(selectedDateRange);

    this.setState({ ...dataState, selectedDateRange });
  };

  // Handle change custom date
  setSelectedDates = selectedDates => {
    const { appLanguage } = this.props;
    const formatDateNumber = DATE_FORMAT[appLanguage].number;
    const selectedDateRange = buildDateRangeFromDate(selectedDates.start, selectedDates.end);
    const startDate = moment(selectedDates.start).format(formatDateNumber);
    const endDate = moment(selectedDates.end).format(formatDateNumber);

    this.setState({ selectedDates, startDate, endDate, selectedDateRange });
  };

  // Handle change month
  handleMonthChange = (month, year) => {
    this.setState({ month, year });
  };

  // Handle start date input data
  handleChangeStarting = value => {
    const { appLanguage } = this.props;
    const formatDateNumber = DATE_FORMAT[appLanguage].number;
    let { selectedDates } = this.state;
    selectedDates.start = moment(value, formatDateNumber).isValid()
      ? moment(value, formatDateNumber).toDate()
      : getStoreDate(new Date());

    this.setState({ startDate: value, selectedDates });
  };

  // Handle blur start date input data
  handleBlurStarting = () => {
    // check if the start date is not valid, change to today/end date
    let { startDate, endDate, selectedDates } = this.state;
    const { appLanguage } = this.props;
    const formatDateNumber = DATE_FORMAT[appLanguage].number;

    const today = getStoreDate(new Date());
    const lastLimitDays = moment(today).add(-NUMBER_DAYS_LIMIT, 'days').toDate();

    // Validate start date
    if (!moment(startDate, formatDateNumber).isValid()) {
      startDate = endDate;
      selectedDates.start = selectedDates.end;
      this.setState({ startDate, selectedDates });
    } else {
      const { start, end } = selectedDates;
      if (moment(start).isAfter(moment(end))) {
        startDate = endDate;
        selectedDates.start = selectedDates.end;
        this.setState({ startDate, selectedDates });
      }
      if (moment(lastLimitDays).isAfter(moment(startDate))) {
        startDate = moment(lastLimitDays).format(formatDateNumber);
        selectedDates.start = lastLimitDays;
        this.setState({ startDate, selectedDates });
      }
    }

    // Update selectedDateRange
    const selectedDateRange = buildDateRangeFromDate(selectedDates.start, selectedDates.end);
    this.setState({ selectedDateRange });
  };

  // Handle end date input data
  handleChangeEnding = value => {
    const { appLanguage } = this.props;
    const formatDateNumber = DATE_FORMAT[appLanguage].number;
    let { selectedDates } = this.state;
    selectedDates.end = moment(value, formatDateNumber).isValid()
      ? moment(value, formatDateNumber).toDate()
      : getStoreDate(new Date());

    this.setState({ endDate: value, selectedDates });
  };

  // Handle blur end date input data
  handleBlurEnding = () => {
    // check if the end date is not valid, change to today/start date
    const { appLanguage } = this.props;
    const formatDateNumber = DATE_FORMAT[appLanguage].number;
    let { endDate, selectedDates } = this.state;
    const today = getStoreDate(new Date());

    // Validate end date
    if (!moment(endDate, formatDateNumber).isValid()) {
      endDate = moment(today).format(formatDateNumber);
      selectedDates.end = today;
      this.setState({ endDate, selectedDates });
    } else {
      const { start, end } = selectedDates;
      if (moment(end).isAfter(today)) {
        endDate = moment(today).format(formatDateNumber);
        selectedDates.end = today;
        this.setState({ endDate, selectedDates });
      }
      if (moment(start).isAfter(moment(end))) {
        endDate = moment(start).format(formatDateNumber);
        selectedDates.end = start;
        this.setState({ endDate, selectedDates });
      }
    }

    // Update selectedDateRange, month, year
    const selectedDateRange = buildDateRangeFromDate(selectedDates.start, selectedDates.end);
    let month = selectedDates.end.getMonth() - 1;
    month = month < 0 ? 11 : month;
    let year = selectedDates.end.getFullYear();
    year = month === 11 ? year - 1 : year;

    this.setState({ selectedDateRange, month, year });
  };

  handleChangeCompare = () => {
    this.setState(({ compare }) => ({
      compare: !compare
    }));
  };

  // Handle apply button
  handleApply = () => {
    const { onChooseDate } = this.props;
    const { selectedDates, compare } = this.state;

    onChooseDate(selectedDates, compare);
    this.togglePopoverActive();
  };

  openReviewAddonModal = () => {
    this.togglePopoverActive();
    this.togglePopoverSelectDateRange();
    this.props.openReviewAddonModal();
  };
}

export const RenderDateRangePicker = withI18n()(RenderDateRangePickerComponent);
